import React, { useEffect, useState } from "react";
import DatePicker from "react-datepicker";
import Select from "react-select";
import "react-datepicker/dist/react-datepicker.css";
import AdminSidebar from "../../components/admin/AdminSidebar";
import Modal from "../../components/shared/Modal";
import SmartDataTable from "../../components/shared/SmartDataTable";
import NumberBalls from "../../components/shared/NumberBalls";
import { Search, Bell, Medal, Play, Loader2, Plus, DollarSign, CheckCircle2, CalendarDays, Trophy } from "lucide-react";
import { resultsApi, poolsApi } from "../../services/api";

function fmtDate(d) {
  return d ? new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "-";
}

const LIMIT = 25;
const EMPTY_RESULT = { pool: null, drawDate: null, n1: "", n2: "", n3: "", n4: "", n5: "", power: "" };

export default function AdminResults() {
  const [results, setResults] = useState([]);
  const [poolOptions, setPoolOptions] = useState([]);
  const [poolOutcomes, setPoolOutcomes] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  const [resultOpen, setResultOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_RESULT);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState("");

  const [checkerOpen, setCheckerOpen] = useState(false);
  const [checkerTarget, setCheckerTarget] = useState(null);
  const [totalPrize, setTotalPrize] = useState("");
  const [checkerRunning, setCheckerRunning] = useState(false);
  const [checkerError, setCheckerError] = useState("");
  const [checkerSuccess, setCheckerSuccess] = useState("");
  const [error, setError] = useState("");

  async function load() {
    setLoading(true);
    setError("");
    const [resultResult, poolResult] = await Promise.allSettled([
      resultsApi.getAll({ limit: 1000, page: 1 }),
      poolsApi.getAll({ limit: 1000, page: 1 }),
    ]);

    if (resultResult.status === "fulfilled") {
      setResults(resultResult.value.data.data || []);
      setTotal(resultResult.value.data.total || 0);
    } else {
      setResults([]);
      setTotal(0);
      setError(resultResult.reason?.response?.data?.message || "Failed to load draw results.");
    }

    if (poolResult.status === "fulfilled") {
      const pools = poolResult.value.data.data || [];
      setPoolOptions(pools.map((p) => ({ value: p._id, label: `${p.poolId} - ${p.name}` })));
      setPoolOutcomes(pools.filter((p) => p.outcome && p.outcome !== "pending"));
    } else {
      setPoolOptions([]);
      setPoolOutcomes([]);
    }

    setLoading(false);
  }

  useEffect(() => { load(); }, []);

  function handleResultClose() {
    setResultOpen(false);
    setForm(EMPTY_RESULT);
    setFormError("");
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!form.pool) { setFormError("Please select a pool."); return; }
    if (!form.drawDate) { setFormError("Draw date is required."); return; }
    const balls = [form.n1, form.n2, form.n3, form.n4, form.n5].map(Number);
    if (balls.some((n) => !n || n < 1 || n > 69)) { setFormError("All 5 white ball numbers must be between 1 and 69."); return; }
    if (!form.power || Number(form.power) < 1 || Number(form.power) > 26) { setFormError("Powerball must be between 1 and 26."); return; }
    setSaving(true);
    try {
      await resultsApi.create({
        pool: form.pool.value,
        drawDate: form.drawDate.toISOString(),
        winningWhiteBalls: balls,
        winningPowerball: Number(form.power),
      });
      handleResultClose();
      load();
    } catch (err) {
      setFormError(err.response?.data?.message || "Failed to save result.");
    } finally {
      setSaving(false);
    }
  }

  function openChecker(result) {
    setCheckerTarget(result);
    setTotalPrize("");
    setCheckerError("");
    setCheckerSuccess("");
    setCheckerOpen(true);
  }

  function handleCheckerClose() {
    setCheckerOpen(false);
    setCheckerTarget(null);
    setCheckerError("");
    setCheckerSuccess("");
  }

  async function handleRunChecker(e) {
    e.preventDefault();
    if (!totalPrize || Number(totalPrize) < 0) { setCheckerError("Please enter a valid prize amount."); return; }
    setCheckerRunning(true);
    setCheckerError("");
    try {
      const res = await resultsApi.runChecker(checkerTarget._id, { totalPrize: Number(totalPrize) });
      setCheckerSuccess(res.data.message || "Winner checker completed successfully.");
      load();
    } catch (err) {
      setCheckerError(err.response?.data?.message || "Checker failed.");
    } finally {
      setCheckerRunning(false);
    }
  }

  const outcomeColumns = [
    { name: "Pool", selector: (row) => row.poolId || "", sortable: true, cell: (row) => <strong className="table-primary">{row.poolId}</strong> },
    { name: "Name", selector: (row) => row.name || "", sortable: true, grow: 1.35 },
    { name: "Outcome", selector: (row) => row.outcome || "", sortable: true, cell: (row) => <span className={`table-status ${row.outcome === "won" ? "live" : "danger"}`}>{row.outcome}</span> },
    { name: "Draw Date", selector: (row) => row.drawDate || "", sortable: true, cell: (row) => fmtDate(row.drawDate) },
    { name: "Note", selector: (row) => row.outcomeNote || "", sortable: true, cell: (row) => <span className="table-muted">{row.outcomeNote || "-"}</span>, grow: 1.5 },
  ];

  const resultColumns = [
    { name: "Pool", selector: (row) => row.pool?.poolId || "", sortable: true, cell: (row) => <strong className="table-primary">{row.pool?.poolId || "-"}</strong> },
    { name: "Name", selector: (row) => row.pool?.name || "", sortable: true, cell: (row) => row.pool?.name || "-", grow: 1.2 },
    { name: "Draw Date", selector: (row) => row.drawDate || "", sortable: true, cell: (row) => fmtDate(row.drawDate) },
    { name: "Numbers", cell: (row) => <NumberBalls numbers={row.winningWhiteBalls} power={row.winningPowerball} />, grow: 1.6 },
    { name: "Checker", selector: (row) => row.checkerRun ? "checked" : "pending", sortable: true, cell: (row) => <span className={`table-status ${row.checkerRun ? "live" : "warn"}`}>{row.checkerRun ? "Checked" : "Pending"}</span> },
    {
      name: "Action",
      button: true,
      cell: (row) => !row.checkerRun ? (
        <button onClick={() => openChecker(row)} title="Run winner checker">
          <Play size={13} /> Run
        </button>
      ) : null,
      minWidth: "120px",
    },
  ];

  return (
    <div className="admin-shell">
      <AdminSidebar />
      <main className="admin-main">
        <header className="admin-topbar">
          <div className="admin-title">Results</div>
          <label className="admin-search"><Search size={15} /><input placeholder="Search results..." /></label>
          <div className="system-live"><i /> System Live</div>
          <button className="icon-button" aria-label="Notifications"><Bell size={18} /><i /></button>
        </header>

        <div className="admin-content">
          {error && <div className="auth-error" style={{ marginBottom: 16 }}>{error}</div>}

          <section className="admin-panel" style={{ padding: 0, marginBottom: 18 }}>
            <div className="panel-heading">
              <h2><Trophy size={18} /> Pool Outcomes
                <span style={{ color: "var(--muted)", fontSize: 13, fontWeight: 400, marginLeft: 8 }}>{poolOutcomes.length} announced</span>
              </h2>
            </div>
            <SmartDataTable
              columns={outcomeColumns}
              data={poolOutcomes}
              loading={loading}
              searchFields={["poolId", "name", "outcome", "outcomeNote"]}
              searchPlaceholder="Search outcomes by pool, status, note..."
              emptyMessage="No pool outcomes announced yet."
              paginationPerPage={LIMIT}
            />
          </section>

          <section className="admin-panel" style={{ padding: 0 }}>
            <div className="panel-heading">
              <h2><Medal size={18} /> Official Draw Numbers
                <span style={{ color: "var(--muted)", fontSize: 13, fontWeight: 400, marginLeft: 8 }}>{total} draws</span>
              </h2>
              <button onClick={() => setResultOpen(true)}><Plus size={14} /> Enter Numbers</button>
            </div>

            <SmartDataTable
              columns={resultColumns}
              data={results}
              loading={loading}
              searchFields={["pool.poolId", "pool.name", "drawDate", (row) => row.checkerRun ? "checked" : "pending"]}
              searchPlaceholder="Search draw numbers by pool, date, checker status..."
              emptyMessage="No official draw numbers entered yet."
              paginationPerPage={LIMIT}
            />
          </section>
        </div>
      </main>

      <Modal open={resultOpen} onClose={handleResultClose} title="Enter Official Numbers" subtitle="Record the official Powerball draw numbers" icon={<Medal size={18} />} size="md">
        <form className="modal-form" onSubmit={handleSubmit}>
          {formError && <div className="auth-error">{formError}</div>}

          <div className="auth-field">
            <label>Pool</label>
            <Select classNamePrefix="lp-select" options={poolOptions} value={form.pool}
              onChange={(v) => { setForm((f) => ({ ...f, pool: v })); setFormError(""); }}
              placeholder="Select pool..." isClearable />
          </div>

          <div className="auth-field">
            <label><CalendarDays size={13} style={{ marginRight: 6 }} />Powerball Draw Date &amp; Time</label>
            <DatePicker selected={form.drawDate}
              onChange={(d) => { setForm((f) => ({ ...f, drawDate: d })); setFormError(""); }}
              showTimeSelect timeFormat="HH:mm" timeIntervals={15}
              dateFormat="MMM d, yyyy h:mm aa" placeholderText="Select draw date and time..." popperPlacement="bottom-start" />
          </div>

          <div>
            <span className="ball-label">Winning White Balls <span style={{ color: "rgba(210,197,175,.45)", fontWeight: 400 }}>(1 - 69)</span></span>
            <div className="ball-inputs">
              {["n1", "n2", "n3", "n4", "n5"].map((key, i) => (
                <input key={key} name={key} type="number" min={1} max={69} value={form[key]}
                  onChange={(e) => { setForm((f) => ({ ...f, [key]: e.target.value })); setFormError(""); }}
                  className="ball-input" placeholder={i + 1} />
              ))}
            </div>
          </div>

          <div style={{ maxWidth: 150 }}>
            <span className="ball-label" style={{ color: "rgba(244,182,192,.8)" }}>Powerball <span style={{ color: "rgba(210,197,175,.45)", fontWeight: 400 }}>(1 - 26)</span></span>
            <input name="power" type="number" min={1} max={26} value={form.power}
              onChange={(e) => { setForm((f) => ({ ...f, power: e.target.value })); setFormError(""); }}
              className="ball-input power" placeholder="PB" />
          </div>

          <div className="modal-actions">
            <button type="button" className="modal-btn-cancel" onClick={handleResultClose}>Cancel</button>
            <button type="submit" className="modal-btn-submit" disabled={saving}>
              {saving ? <><Loader2 size={14} className="spin" /> Saving...</> : <><Medal size={14} /> Save Numbers</>}
            </button>
          </div>
        </form>
      </Modal>

      <Modal open={checkerOpen} onClose={handleCheckerClose}
        title="Run Winner Checker"
        subtitle={checkerTarget ? `${checkerTarget.pool?.poolId || ""} - Draw ${fmtDate(checkerTarget.drawDate)}` : ""}
        icon={<Play size={18} />} size="sm">
        {checkerSuccess ? (
          <div style={{ display: "grid", gap: 20 }}>
            <div className="auth-success" style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <CheckCircle2 size={16} /> {checkerSuccess}
            </div>
            <div className="modal-actions" style={{ marginTop: 0 }}>
              <button className="modal-btn-submit" onClick={handleCheckerClose}>Done</button>
            </div>
          </div>
        ) : (
          <form className="modal-form" onSubmit={handleRunChecker}>
            {checkerError && <div className="auth-error">{checkerError}</div>}
            <p style={{ color: "var(--muted)", fontSize: 14, margin: 0, lineHeight: 1.6 }}>
              The checker compares all pool tickets against the official numbers. If any ticket wins, the prize is divided equally among all active pool members.
            </p>
            <div className="auth-field">
              <label><DollarSign size={13} style={{ marginRight: 6 }} />Total Prize Amount ($)</label>
              <input type="number" min={0} step="0.01" value={totalPrize}
                onChange={(e) => { setTotalPrize(e.target.value); setCheckerError(""); }}
                placeholder="e.g. 300000" autoFocus />
              <small style={{ color: "var(--muted)", fontSize: 11 }}>Divided equally among all active pool members</small>
            </div>
            <div className="modal-actions">
              <button type="button" className="modal-btn-cancel" onClick={handleCheckerClose}>Cancel</button>
              <button type="submit" className="modal-btn-submit" disabled={checkerRunning}>
                {checkerRunning ? <><Loader2 size={14} className="spin" /> Running...</> : <><Play size={14} /> Run Checker</>}
              </button>
            </div>
          </form>
        )}
      </Modal>
    </div>
  );
}
